/** Saved appearance for the authenticated CRM. Public client pages never read this. */

export const CRM_THEME_STORAGE_KEY = 'lctf-crm-theme';

export type CrmTheme = 'light' | 'dark';
export type CrmThemeChoice = CrmTheme | null;

const PUBLIC_EXACT = new Set([
  '/briefings',
  '/questionnaire',
  '/structural-styles',
  '/journey',
  '/shed-calc',
]);

/** Prefixes that match a public route and anything nested under it. */
const PUBLIC_PREFIXES = [
  '/engagement/',
  '/proposal/',
  '/offer/',
  '/briefing/',
  '/change-order/',
  '/portal/',
  '/plan-share/',
  '/crew-jobs',
  '/crew-onboard/',
  '/invite/',
];

export function isPublicCrmPath(pathname: string): boolean {
  const path = (pathname || '/').split('?')[0].split('#')[0] || '/';
  const normalized = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  if (PUBLIC_EXACT.has(normalized)) return true;
  return PUBLIC_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) return normalized.startsWith(prefix) || `${normalized}/` === prefix;
    return normalized === prefix || normalized.startsWith(`${prefix}/`);
  });
}

export function readStoredThemeChoice(storage?: Storage | null): CrmThemeChoice {
  const store = storage === undefined
    ? (typeof localStorage !== 'undefined' ? localStorage : null)
    : storage;
  if (!store) return null;
  try {
    const value = store.getItem(CRM_THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark') return value;
  } catch {
    /* private browsing */
  }
  return null;
}

export function writeStoredThemeChoice(theme: CrmTheme, storage?: Storage | null): void {
  const store = storage === undefined
    ? (typeof localStorage !== 'undefined' ? localStorage : null)
    : storage;
  if (!store) return;
  try {
    store.setItem(CRM_THEME_STORAGE_KEY, theme);
  } catch {
    /* private browsing */
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveCrmTheme(choice: CrmThemeChoice, systemDark: boolean): CrmTheme {
  if (choice) return choice;
  return systemDark ? 'dark' : 'light';
}

export function applyCrmTheme(theme: CrmTheme, root?: HTMLElement): void {
  const el = root ?? (typeof document !== 'undefined' ? document.documentElement : null);
  if (!el) return;
  if (theme === 'dark') el.setAttribute('data-theme', 'dark');
  else el.removeAttribute('data-theme');
}

export function clearCrmTheme(root?: HTMLElement): void {
  const el = root ?? (typeof document !== 'undefined' ? document.documentElement : null);
  el?.removeAttribute('data-theme');
}
